import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { CopyOutlined, DeleteOutlined, EditOutlined, GiftOutlined, LockOutlined, PlusOutlined, RocketOutlined } from '@ant-design/icons';
import { usePermify } from '@permify/react-role';
import CampaignRequestorRegionGroupsDialog from './CampaignRequestorRegionGroupsDialog';
import CampaignRequestorTargetingGroupsDialog from './CampaignRequestorTargetingGroupsDialog';
import { CampaignRequestorAudience } from './CampaignRequestorAudience';
import { CampaignRequestorAdGroup } from './CampaignRequestorAdGroup';
import { CampaignRequestorVideoCreatives } from './CampaignRequestorVideoCreatives';
import { CampaignRequestorTargetingRegionTable } from './CampaignRequestorTargetingRegionTable';
import { CampaignRequestorTargetingLocalTable } from './CampaignRequestorTargetingLocalTable';
import { CampaignRequestorCountriesTable } from './CampaignRequestorCountriesTable';
import CampaignRequestorNexxenSegmentsDialog from './CampaignRequestorNexxenSegmentsDialog';
import CampaignRequestorInventoryDialog from './CampaignRequestorInventoryDialog';
import { sspPartners } from '../../../core/utils/constants/constants';
import moment from 'moment-timezone';
import { useCampaignRequestorContext } from '../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { useTimezone } from '../../../core/hooks/useTimezone';
import { isPercentageModel, getMinimumCpmForCampaignType } from '../../../core/utils/pricing/cpmHelper';
import { validateLineItemForDelivery } from './CampaignRequestorUtil';
import { isFlightBudgetRestricted } from './utils/campaignValidation';

const { Title, Text } = Typography;
const { Panel } = Collapse;

// Safe DatePicker wrapper that ensures moment objects and normalizes dates to user timezone then UTC
const SafeDatePicker = ({ value, onChange, normalizeToEndOfDay = false, timezone = 'Etc/GMT-8', ...props }) => {
  const safeValue = value && typeof value === 'object' && value._isAMomentObject
    ? moment(value).tz(timezone)
    : value
      ? moment(value).tz(timezone)
      : null;

  const handleChange = (date) => {
    if (!date) {
      onChange && onChange(date);
      return;
    }

    // Normalize to start or end of day in user timezone, then convert to UTC
    const normalizedDate = normalizeToEndOfDay
      ? moment.tz(date.format('YYYY-MM-DD'), timezone).endOf('day').utc()
      : moment.tz(date.format('YYYY-MM-DD'), timezone).startOf('day').utc();

    onChange && onChange(normalizedDate);
  };

  return <DatePicker {...props} value={safeValue} onChange={handleChange} />;
};

const CampaignRequestorLineItem = ({
  formValues,
  derivedState,
  helpers,
  name,
  fieldKey,
  restField,
  index,
  campaignIndex,
  add,
  remove,
  currentCampaignOrder,
  isAdvancedMode = false, // Advanced Mode prop for conditional features
  campaignDsp = null, // DSP selected at campaign level
  campaignValueAdd = false, // Campaign-level value add status for inheritance
  showOnlyDeliveryItems = false, // Filter to show only line items marked for delivery
  isBulkCloneMode = false, // Bulk clone mode active
  hasTraffickerOrProcessorPermission = false, // User has isTrafficker or isProcessor role
  isNotDraftStatus = false, // Campaign status is not DRAFT
  isSelected = false, // Line item is selected for bulk clone
  onSelectionChange = null, // Callback when selection changes
}) => {
  // Get context for pricing model
  const { selectedAdvertiser, currentCampaignOrder: contextCampaignOrder } = useCampaignRequestorContext();
  const { timezone } = useTimezone();

  // Check if pricing model is Percentage
  // For existing campaigns, use the saved pricingModel; for new campaigns, use advertiser's pricing model
  const hideCpmFields = useMemo(() => {
    // Use the prop if available, otherwise fall back to context
    const campaignOrder = currentCampaignOrder || contextCampaignOrder;
    if (campaignOrder?.pricingModel) {
      return campaignOrder.pricingModel === 'Percentage';
    }
    return isPercentageModel(selectedAdvertiser);
  }, [currentCampaignOrder, contextCampaignOrder, selectedAdvertiser]);

  // Extract reactive values from formValues prop
  // Parent already watches form changes and transforms formValues.campaigns to be the lineItems array
  // This ensures we always have reactive data that triggers re-renders
  const { startDate: campaignStartDate, endDate: campaignEndDate, type: campaignType, campaigns: campaignsFromProps = [], campaignBudget = 0 } = formValues;

  // Get current line item data from the transformed formValues prop
  const lineItem = campaignsFromProps[index] || {};

  const {
    name: lineItemName,
    targetingRegions = [],
    targetingLocations = [],
    startDate: lineItemStartDate,
    endDate: lineItemEndDate,
    targetingType: initialTargetingType,
  } = lineItem;

  const [showRegionsModal, setShowRegionsModal] = useState(false);
  const [showTargetingGroupsModal, setShowTargetingGroupsModal] = useState(false);
  const [showNexxenSegmentsDialog, setShowNexxenSegmentsDialog] = useState(false);
  const [showInventoryDialog, setShowInventoryDialog] = useState(false);
  const [shouldDisableOriginEditing, setShouldDisableOriginEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [canEditCpm, setCanEditCpm] = useState(false);
  const { isAuthorized } = usePermify();

  // State for Flight Schedule (Advanced Mode)
  // Note: GraphQL returns 'flightSchedules' (plural), component uses 'flightSchedule' (singular) for state
  const [flightSchedule, setFlightSchedule] = useState(lineItem.flightSchedules || lineItem.flightSchedule || []);
  const [hasInitializedFlightSchedule, setHasInitializedFlightSchedule] = useState(false);

  // Initialize flight schedule from existing line item data ONCE only
  useEffect(() => {
    const existingFlights = lineItem.flightSchedules || lineItem.flightSchedule || [];
    if (existingFlights.length > 0 && !hasInitializedFlightSchedule) {
      setFlightSchedule(existingFlights);
      setHasInitializedFlightSchedule(true);
    }
  }, [lineItem.flightSchedules, lineItem.flightSchedule, hasInitializedFlightSchedule]);

  // Check if user can edit origin line items
  useEffect(() => {
    const checkEditingPermissions = async () => {
      const campaignStatus = currentCampaignOrder?.status;

      // In DRAFT status, allow all editing (hide origin tag and enable all fields)
      if (campaignStatus === 'DRAFT') {
        setShouldDisableOriginEditing(false);
        return;
      }

      const isAdOps = await isAuthorized(['isTrafficker', 'isProcessor'], []);
      const isClient = await isAuthorized(['isSelfService'], []);

      // Client/Customer users can edit everything
      if (isClient && !isAdOps) {
        setShouldDisableOriginEditing(false);
        return;
      }

      // Ad Ops users cannot edit origin items (when status is not DRAFT)
      if (isAdOps) {
        setShouldDisableOriginEditing(true);
        return;
      }

      // Default: allow editing (includes legacy cases)
      setShouldDisableOriginEditing(false);
    };

    checkEditingPermissions();
  }, [isAuthorized, currentCampaignOrder]);

  // Check if user can edit CPM fields (only Ad Ops)
  useEffect(() => {
    const checkCpmPermissions = async () => {
      const hasAdOpsPermission = await isAuthorized(['isTrafficker', 'isProcessor'], []);
      setCanEditCpm(hasAdOpsPermission);
    };
    checkCpmPermissions();
  }, [isAuthorized]);

  const [targetingType, setTargetingType] = useState(initialTargetingType);

  // Sync targetingType state with lineItem when it changes
  useEffect(() => {
    if (lineItem.targetingType && lineItem.targetingType !== targetingType) {
      setTargetingType(lineItem.targetingType);
    }
  }, [lineItem.targetingType, targetingType]);

  // Sync flight schedule state to form values whenever it changes
  // Note: We use a ref to get current campaignsFromProps to avoid infinite loop
  const campaignsRef = useRef(campaignsFromProps);
  campaignsRef.current = campaignsFromProps;

  // Detect if line item has pending changes (status-based detection)
  const isLineItemReadyForUpdate = useMemo(() => {
    // Check if status is PENDING (indicates changes waiting to be applied)
    const status = lineItem?.status;
    return status === 'PLAY_PENDING' || status === 'PAUSED_PENDING';
  }, [lineItem.status]);

  useEffect(() => {
    // Helper function to compare flight schedules (handles moment objects)
    const flightSchedulesEqual = (a, b) => {
      if (!a && !b) return true;
      if (!a || !b) return false;
      if (a.length !== b.length) return false;

      return a.every((flightA, idx) => {
        const flightB = b[idx];
        if (!flightB) return false;

        // Compare dates by converting to ISO strings
        const startDateA = flightA.startDate ? moment(flightA.startDate).format('YYYY-MM-DD') : null;
        const startDateB = flightB.startDate ? moment(flightB.startDate).format('YYYY-MM-DD') : null;
        const endDateA = flightA.endDate ? moment(flightA.endDate).format('YYYY-MM-DD') : null;
        const endDateB = flightB.endDate ? moment(flightB.endDate).format('YYYY-MM-DD') : null;

        return startDateA === startDateB &&
               endDateA === endDateB &&
               flightA.budget === flightB.budget &&
               flightA.cpm === flightB.cpm;
      });
    };

    const currentFormFlightSchedule = campaignsRef.current[index]?.flightSchedule || [];
    const hasChanged = !flightSchedulesEqual(currentFormFlightSchedule, flightSchedule);

    if (hasChanged) {
      // Get full form values to update at the correct nested path
      const fullFormValues = helpers.form.getFieldsValue();
      const allCampaigns = fullFormValues.campaigns || [];

      // Update the correct nested path: campaigns[campaignIndex].lineItems[index].flightSchedule
      const updatedAllCampaigns = allCampaigns.map((campaign, cIdx) => {
        if (cIdx === campaignIndex) {
          return {
            ...campaign,
            lineItems: (campaign.lineItems || []).map((lineItem, liIdx) => {
              if (liIdx === index) {
                return {
                  ...lineItem,
                  flightSchedule: flightSchedule,
                };
              }
              return lineItem;
            }),
          };
        }
        return campaign;
      });

      // Set the full form values with the correct structure
      helpers.form.setFieldsValue({
        campaigns: updatedAllCampaigns,
      });

      // Trigger form validation to update validationState immediately
      // This ensures "Continue to Review" button state updates in real-time
      helpers.form.validateFields().catch(() => {
        // Silently catch validation errors - we just want to trigger the validation check
      });

    }
  }, [flightSchedule, helpers, index, campaignIndex]);

  const handleCloneLineItem = ({ event, index: cloneIndex }) => {
    event.preventDefault();

    // campaignsFromProps is the lineItems array for this campaign
    const cloneItemSource = JSON.parse(JSON.stringify(campaignsFromProps[cloneIndex]));

    let newLineItem = {
      ...cloneItemSource,
      impressions: 0,
    };

    // Remove $ suffix from line item name if it exists before adding (Copy)
    const baseLineItemName = newLineItem.name ? newLineItem.name.replace(/-\$\s*$/, '').trim() : '';
    // Inherit campaign's valueAdd status
    const inheritedValueAdd = campaignValueAdd || false;
    // Set line item name with -$ suffix if campaign is value add
    newLineItem.name = inheritedValueAdd ? `${baseLineItemName} (Copy)-$` : `${baseLineItemName} (Copy)`;
    delete newLineItem.id;

    // Exclude isOrigin from cloned items
    delete newLineItem.isOrigin;
    // Inherit campaign's valueAdd status (don't delete it)
    newLineItem.valueAdd = inheritedValueAdd;

    // Cloned items should never be auto-marked for delivery.
    // The user must explicitly mark them via the rocket button.
    delete newLineItem.markedForDelivery;

    // Check if there adGroup and adGroupCreatives
    if (newLineItem.adGroup && newLineItem.adGroup.id) {
      delete newLineItem.adGroup.id;
      if (newLineItem.adGroup.adGroupCreatives && newLineItem.adGroup.adGroupCreatives.length) {
        newLineItem.adGroup.adGroupCreatives = newLineItem.adGroup.adGroupCreatives.map(
          (adGroupCreative) => {
            delete adGroupCreative.id;
            if (adGroupCreative.medias && adGroupCreative.medias.length) {
              adGroupCreative.medias = adGroupCreative.medias.map((media) => {
                delete media.id;
                return media;
              });
            }
            return adGroupCreative;
          }
        );
      }
    }

    // Check if there are firstPartyAudiences
    if (newLineItem.firstPartyDataAudiences && newLineItem.firstPartyDataAudiences.length) {
      newLineItem.firstPartyDataAudiences = newLineItem.firstPartyDataAudiences.map((audience) => {
        delete audience.id;
        return audience;
      });
    }

    // Check if there are targetingRegions
    if (newLineItem.targetingRegions && newLineItem.targetingRegions.length) {
      newLineItem.targetingRegions = newLineItem.targetingRegions.map((region) => {
        delete region.id;
        return region;
      });
    }

    // Check if there are targetingLocations
    if (newLineItem.targetingLocations && newLineItem.targetingLocations.length) {
      newLineItem.targetingLocations = newLineItem.targetingLocations.map((location) => {
        delete location.id;
        return location;
      });
    }

    // Handle flight schedules: Always start with empty array when cloning
    // User should manually add flight schedules to the cloned line item
    newLineItem.flightSchedule = [];
    // Clean up the plural version to avoid confusion
    delete newLineItem.flightSchedules;

    newLineItem = {
      ...newLineItem,
      startDate: newLineItem.startDate ? moment(newLineItem.startDate) : null,
      endDate: newLineItem.endDate ? moment(newLineItem.endDate) : null,
      flightSchedule: newLineItem.flightSchedule
        ? newLineItem.flightSchedule.map((flight) => ({
            ...flight,
            startDate: flight.startDate ? moment(flight.startDate) : null,
            endDate: flight.endDate ? moment(flight.endDate) : null,
          }))
        : [],
      adGroup:
        newLineItem.adGroup && newLineItem.adGroup.adGroupCreatives
          ? {
              ...newLineItem.adGroup,
              adGroupCreatives: newLineItem.adGroup.adGroupCreatives.map((adGroupCreative) => {
                return {
                  ...adGroupCreative,
                  startDate: adGroupCreative.startDate ? moment(adGroupCreative.startDate) : null,
                  endDate: adGroupCreative.endDate ? moment(adGroupCreative.endDate) : null,
                };
              }),
            }
          : null,
    };

    // Use only setFieldsValue to update the array — calling both add() and
    // setFieldsValue causes a form state conflict that can reset existing items.
    const updatedLineItems = [...campaignsFromProps, newLineItem];
    helpers.setFieldsValue({
      campaigns: updatedLineItems,
    });

    // Show success message
    message.success('Line item cloned successfully');

    // Calculate the new item's index (it will be added to the end of the array)
    const newItemIndex = campaignsFromProps.length;

    // Scroll to the newly cloned line item and trigger validation
    setTimeout(() => {
      // Smooth scroll to the newly cloned item (show header at top)
      const newItemElement = document.getElementById(`requester-line-item-${campaignIndex}-${newItemIndex}`);
      if (newItemElement) {
        newItemElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      // Trigger validation to show error states for the new cloned line item
      helpers.validateFields().catch(() => {
        // Ignore validation errors, we just want to show the error states
      });
    }, 100);
  };

  const handleValueAdd = ({ index }) => {
    // If campaign is value add, line items cannot be individually toggled
    if (campaignValueAdd) {
      return;
    }

    // Update lineItem valueAdd status and auto-postfix $ symbol to name
    const updatedCampaigns = campaignsFromProps.map((campaign, i) => {
      if (i === index) {
        const newValueAddStatus = !campaign.valueAdd;
        let updatedName = campaign.name;

        // Toggle ON: Add -$ to the name
        if (newValueAddStatus) {
          updatedName = campaign.name.trim();
          if (!updatedName.endsWith('-$')) {
            updatedName = updatedName + '-$';
          }
        }
        // Toggle OFF: Remove -$ from the name
        else {
          updatedName = campaign.name.trim();
          if (updatedName.endsWith('-$')) {
            updatedName = updatedName.slice(0, -2).trim();
          }
        }

        return {
          ...campaign,
          name: updatedName,
          valueAdd: newValueAddStatus,
        };
      }
      return campaign;
    });
    helpers.setFieldsValue({
      campaigns: updatedCampaigns,
    });
  };

  const handleLineItemNameChange = (e) => {
    // Always strip $ symbols - they should only be added programmatically
    const cleanValue = e.target.value.replace(/\$/g, '');

    // Only update if the value actually changed (to avoid unnecessary re-renders)
    if (cleanValue !== e.target.value) {
      const updatedCampaigns = campaignsFromProps.map((campaign, idx) =>
        idx === index ? { ...campaign, name: cleanValue } : campaign
      );
      helpers.setFieldsValue({ campaigns: updatedCampaigns });
    }
  };

  const handleLineItemNameBlur = () => {
    // Re-add $ to Value Add items if it was removed during editing
    if (lineItem.valueAdd) {
      const currentName = lineItem.name.trim();
      // Only add -$ if it doesn't already end with -$
      if (!currentName.endsWith('-$')) {
        const updatedCampaigns = campaignsFromProps.map((campaign, idx) =>
          idx === index ? { ...campaign, name: currentName + '-$' } : campaign
        );
        helpers.setFieldsValue({ campaigns: updatedCampaigns });
      }
    }
  };

  const handleMarkForDelivery = ({ index }) => {
    // Update lineItem markedForDelivery status
    const updatedCampaigns = campaignsFromProps.map((campaign, i) => {
      if (i === index) {
        return {
          ...campaign,
          markedForDelivery: !campaign.markedForDelivery,
        };
      }
      return campaign;
    });
    helpers.setFieldsValue({
      campaigns: updatedCampaigns,
    });
  };

  /**
   * Validates if all required fields for delivery are complete
   * Uses useMemo for performance optimization
   * @returns {Object} { isValid: boolean, missingFields: string[] }
   */
  const validateDeliveryReadiness = useMemo(() => {
    return validateLineItemForDelivery(lineItem, hideCpmFields);
  }, [lineItem, hideCpmFields]);

  /**
   * Generate dynamic tooltip message for the Mark for Delivery button
   * Shows different messages based on button state and validation
   */
  const deliveryButtonTooltip = useMemo(() => {
    // If already marked for delivery, show confirmation
    if (lineItem.markedForDelivery) {
      return 'Marked for Delivery';
    }

    // If validation fails, show missing fields
    if (!validateDeliveryReadiness.isValid) {
      const fieldsList = validateDeliveryReadiness.missingFields.join(', ');
      return `Cannot mark for delivery. Missing required fields: ${fieldsList}`;
    }

    // Default state - ready to mark
    return 'Mark for Delivery';
  }, [lineItem.markedForDelivery, validateDeliveryReadiness]);

  /**
   * Auto-unmark line item for delivery when required fields become invalid
   * Prevents invalid line items from being marked for delivery
   */
  useEffect(() => {
    // Only auto-unmark if line item is currently marked AND validation fails
    if (lineItem.markedForDelivery && !validateDeliveryReadiness.isValid) {
      // Update the campaigns array to unmark this line item
      const updatedCampaigns = campaignsFromProps.map((campaign, i) => {
        if (i === index) {
          return {
            ...campaign,
            markedForDelivery: false,
          };
        }
        return campaign;
      });

      helpers.setFieldsValue({
        campaigns: updatedCampaigns,
      });

      // Notify user about auto-unmarking
      message.warning(
        `Line item "${lineItem.name || `Line Item ${index + 1}`}" unmarked for delivery due to missing fields: ${validateDeliveryReadiness.missingFields.join(', ')}`
      );
    }
  }, [lineItem, validateDeliveryReadiness, campaignsFromProps, index, helpers]);

  const handleDeleteLineItem = (index) => {
    const newCampaigns = campaignsFromProps.filter((_, i) => i !== index);
    helpers.setFieldsValue({
      campaigns: newCampaigns,
    });
  };

  /**
   * Auto-adjust end dates when flight schedule end date exceeds parent dates
   * If flight schedule end date > line item/campaign/campaign order end date,
   * automatically extend the parent end dates to match
   */
  const autoAdjustEndDatesFromFlightSchedule = (updatedFlightSchedule) => {
    if (!updatedFlightSchedule || updatedFlightSchedule.length === 0) return;

    // Find the furthest end date among all flight schedules
    let furthestEndDate = null;
    updatedFlightSchedule.forEach((flight) => {
      if (flight.endDate) {
        const flightEnd = moment(flight.endDate);
        if (!furthestEndDate || flightEnd.isAfter(furthestEndDate)) {
          furthestEndDate = flightEnd;
        }
      }
    });

    if (!furthestEndDate) return;

    // Get the full form values to access campaign order level data
    const fullFormValues = helpers.form.getFieldsValue();
    const allCampaigns = fullFormValues.campaigns || [];

    // Track what needs to be updated
    let needsLineItemUpdate = false;
    let needsCampaignUpdate = false;
    let needsCampaignOrderUpdate = false;

    // Check if line item end date needs adjustment
    const currentLineItemEnd = lineItemEndDate ? moment(lineItemEndDate) : null;
    if (!currentLineItemEnd || furthestEndDate.isAfter(currentLineItemEnd)) {
      needsLineItemUpdate = true;
    }

    // Check if campaign end date needs adjustment (from the actual campaign in the form)
    const currentCampaign = allCampaigns[campaignIndex];
    const currentCampaignEnd = currentCampaign?.endDate ? moment(currentCampaign.endDate) : null;
    if (!currentCampaignEnd || furthestEndDate.isAfter(currentCampaignEnd)) {
      needsCampaignUpdate = true;
    }

    // Check if campaign order end date needs adjustment
    const currentCampaignOrderEnd = fullFormValues.endDate ? moment(fullFormValues.endDate) : null;
    if (!currentCampaignOrderEnd || furthestEndDate.isAfter(currentCampaignOrderEnd)) {
      needsCampaignOrderUpdate = true;
    }

    // Apply updates if needed
    if (needsLineItemUpdate || needsCampaignUpdate || needsCampaignOrderUpdate) {
      // Build updates for the full form
      const fullFormUpdates = {};

      // Update campaigns array with line item and campaign end date changes
      if (needsLineItemUpdate || needsCampaignUpdate) {
        const updatedAllCampaigns = allCampaigns.map((campaign, cIdx) => {
          if (cIdx === campaignIndex) {
            // Update this campaign
            let updatedCampaign = { ...campaign };

            // Update campaign end date if needed
            if (needsCampaignUpdate) {
              updatedCampaign.endDate = furthestEndDate;
            }

            // Update line item end date if needed
            if (needsLineItemUpdate && updatedCampaign.lineItems) {
              updatedCampaign.lineItems = updatedCampaign.lineItems.map((li, liIdx) => {
                if (liIdx === index) {
                  return { ...li, endDate: furthestEndDate };
                }
                return li;
              });
            }

            return updatedCampaign;
          }
          return campaign;
        });
        fullFormUpdates.campaigns = updatedAllCampaigns;
      }

      // Update campaign order end date if needed
      if (needsCampaignOrderUpdate) {
        fullFormUpdates.endDate = furthestEndDate;
      }

      // Apply all updates to the full form
      if (Object.keys(fullFormUpdates).length > 0) {
        helpers.form.setFieldsValue(fullFormUpdates);

        // Show notification to user about auto-adjustment
        const adjustedItems = [];
        if (needsLineItemUpdate) adjustedItems.push('line item');
        if (needsCampaignUpdate) adjustedItems.push('campaign');
        if (needsCampaignOrderUpdate) adjustedItems.push('campaign order');

        message.info(
          `End date auto-adjusted for ${adjustedItems.join(', ')} to match flight schedule end date (${furthestEndDate.format('MM/DD/YYYY')})`,
          4
        );
      }
    }
  };

  const startDisabledDate = (date) => {
    if (!campaignStartDate || !campaignEndDate) {
      return true;
    }

    const effectiveEndDate = lineItemEndDate || campaignEndDate;
    const endDateRestriction =
      effectiveEndDate &&
      (date.isAfter(moment(effectiveEndDate)) ||
        date.isSameOrAfter(moment(effectiveEndDate).clone().subtract(2, 'days')));

    const startDateRestriction = campaignStartDate && date.isBefore(moment(campaignStartDate));

    return endDateRestriction || startDateRestriction;
  };

  const endDisabledDate = (date) => {
    if (!campaignStartDate || !campaignEndDate) {
      return true;
    }

    const effectiveStartDate = lineItemStartDate || campaignStartDate;
    const startDateRestriction =
      effectiveStartDate && date.isBefore(moment(effectiveStartDate).clone().add(2, 'days'));

    const endDateRestriction =
      campaignEndDate && date.isAfter(moment(campaignEndDate).endOf('day'));

    return startDateRestriction || endDateRestriction;
  };

  const calculateImpressions = ({ budget, cpm }) => {
    if (cpm <= 0) {
      return 0;
    }
    return Math.round((budget / cpm) * 1000);
  };

  // Filter logic: If filter is active and line item is not marked for delivery, hide it
  if (showOnlyDeliveryItems && !lineItem.markedForDelivery) {
    return null;
  }

  return (
    <div id={`requester-line-item-${campaignIndex}-${index}`}>
      <Collapse bordered={false} defaultActiveKey={[0]} key={index}>
        <Panel
          open={true}
          header={
            <Space>
              {isBulkCloneMode && (
                <Checkbox
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    if (onSelectionChange) {
                      onSelectionChange(index, e.target.checked);
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              <Title level={5}>{lineItemName}</Title>
              {shouldDisableOriginEditing && lineItem.isOrigin && (
                <Tag icon={<LockOutlined />} color="blue">
                  Origin (Client Created)
                </Tag>
              )}
              {lineItem.markedForDelivery && (
                <Tag icon={<RocketOutlined />} color="green">
                  Delivery
                </Tag>
              )}
              {lineItem.valueAdd && (
                <Tag icon={<GiftOutlined />} color="purple">
                  Value Add
                </Tag>
              )}
              {isLineItemReadyForUpdate && (
                <Tooltip title="Line item has been modified and is ready to be updated when re-launched">
                  <Tag icon={<EditOutlined />} color="orange">
                    Ready for Update
                  </Tag>
                </Tooltip>
              )}
            </Space>
          }
          extra={
            <Space size="small">
              {/* Value Add button - visible when user has permission, status is not DRAFT, and line item is not origin */}
              {hasTraffickerOrProcessorPermission && isNotDraftStatus && !lineItem.isOrigin && (
                <Tooltip
                  title={
                    campaignValueAdd
                      ? 'Controlled by campaign value add status'
                      : (lineItem.valueAdd ? 'Marked as Value Add' : 'Mark as Value Add')
                  }
                >
                  <Button
                    shape="circle"
                    icon={<GiftOutlined />}
                    disabled={campaignValueAdd}
                    style={{
                      backgroundColor: lineItem.valueAdd ? '#722ed1' : '#fff',
                      borderColor: '#722ed1',
                      color: lineItem.valueAdd ? '#fff' : '#722ed1',
                      cursor: campaignValueAdd ? 'not-allowed' : 'pointer',
                      opacity: campaignValueAdd ? 0.6 : 1,
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!campaignValueAdd) {
                        handleValueAdd({ index });
                      }
                    }}
                  />
                </Tooltip>
              )}
              {/* Mark for Delivery button - visible when user has permission and status is not DRAFT */}
              {hasTraffickerOrProcessorPermission && isNotDraftStatus && (
                <Tooltip title={deliveryButtonTooltip}>
                  <Button
                    shape="circle"
                    icon={<RocketOutlined />}
                    disabled={!lineItem.markedForDelivery && !validateDeliveryReadiness.isValid}
                    style={{
                      backgroundColor: lineItem.markedForDelivery ? '#52c41a' : '#fff',
                      borderColor: lineItem.markedForDelivery ? '#52c41a' : '#d9d9d9',
                      color: lineItem.markedForDelivery
                        ? '#fff'
                        : (validateDeliveryReadiness.isValid ? '#52c41a' : 'rgba(0, 0, 0, 0.25)'),
                      cursor: (!lineItem.markedForDelivery && !validateDeliveryReadiness.isValid)
                        ? 'not-allowed'
                        : 'pointer',
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleMarkForDelivery({ index });
                    }}
                  />
                </Tooltip>
              )}
              <Tooltip title={`Delete Line Item ${index + 1}`}>
                <Button
                  shape="circle"
                  disabled={!index || (isAdvancedMode && lineItem.isOrigin)}
                  icon={<DeleteOutlined />}
                  danger
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteLineItem(index);
                  }}
                />
              </Tooltip>
              <Tooltip title={`Clone Line Item ${index + 1}`}>
                <Button
                  shape="circle"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloneLineItem({ event: e, index });
                  }}
                  icon={<CopyOutlined />}
                  style={{
                    backgroundColor: '#fff',
                    borderColor: '#1890ff',
                    color: '#1890ff',
                  }}
                />
              </Tooltip>
            </Space>
          }
        >
          <Card bodyStyle={{ padding: 0 }}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              type="card"
              style={{ padding: '16px 24px 0' }}
              items={[
                {
                  key: 'details',
                  label: 'Line Item Details',
                  children: (
                    <Space direction="vertical" style={{ width: '100%', paddingBottom: 24 }} size="large">
                      <Row gutter={20}>
                        <Col span={12}>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text>Line Item Name</Text>
                    <Text type="secondary">The name you will identify this line item by</Text>
                    <Form.Item
                      {...restField}
                      name={[name, 'name']}
                      fieldKey={[fieldKey, 'name']}
                      rules={[{ required: true, message: 'Missing Line Item Name' }]}
                    >
                      <Input placeholder="Line Item Name" maxLength={100} onChange={handleLineItemNameChange} onBlur={handleLineItemNameBlur} disabled={shouldDisableOriginEditing && lineItem.isOrigin} />
                    </Form.Item>
                  </Space>
                </Col>
              </Row>

              <Card title="DURATION">
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  {(() => {
                    const effectiveStartDate = lineItemStartDate || campaignStartDate;
                    const today = moment.tz(timezone).startOf('day');
                    const startDate = moment(effectiveStartDate).tz(timezone).startOf('day');
                    const campaignStatus = currentCampaignOrder?.status;

                    if (effectiveStartDate && startDate.isBefore(today)) {
                      if (campaignStatus === 'LIVE' || campaignStatus === 'LIVE_PENDING' || campaignStatus === 'LIVE_APPROVED') {
                        return null;
                      }
                      
                      return (
                        <Alert
                          message="Start Date Notice"
                          description="The start date is in the past."
                          type="warning"
                          showIcon
                          style={{ marginBottom: 16 }}
                        />
                      );
                    }
                    return null;
                  })()}

                  <Row gutter={20}>
                    <Col span={10}>
                      <Row>
                        <Col span={12}>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text>Start Date</Text>
                            <Text type="secondary">Leave blank for ASAP </Text>
                            <Form.Item
                              {...restField}
                              name={[name, 'startDate']}
                              fieldKey={[fieldKey, 'startDate']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Missing Start Date',
                                },
                              ]}
                            >
                              <SafeDatePicker timezone={timezone} disabled={shouldDisableOriginEditing && lineItem.isOrigin} />
                            </Form.Item>
                          </Space>
                        </Col>
                        <Col span={12}>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text>End Date</Text>
                            <Text type="secondary">Date campaign will end</Text>
                            <Form.Item
                              {...restField}
                              name={[name, 'endDate']}
                              fieldKey={[fieldKey, 'endDate']}
                              rules={[{ required: true, message: 'Missing End Date' }]}
                            >
                              <SafeDatePicker normalizeToEndOfDay={true} timezone={timezone} disabled={shouldDisableOriginEditing && lineItem.isOrigin} />
                            </Form.Item>
                          </Space>
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  <Row gutter={20}>
                    <Col span={10}>
                      <Row>
                        <Col span={24}>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text>Line Item Budget</Text>
                            <Text type="secondary">
                              Maximum amount this line item will spend over its duration
                            </Text>
                            <div>
                              <Input.Group compact>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'budgetTotal']}
                                  fieldKey={[fieldKey, 'budgetTotal']}
                                  style={{ width: '30%' }}
                                  rules={[
                                    {
                                      required: true,
                                      message: 'Missing Line Item Budget',
                                    },
                                    {
                                      validator: (_, value) => {
                                        if (value > 0) {
                                          return Promise.resolve();
                                        }
                                        return Promise.reject(
                                          'Line Item Budget must be greater than zero'
                                        );
                                      },
                                    },
                                  ]}
                                >
                                  <InputNumber
                                    addonBefore={<>$</>}
                                    min={0}
                                    step={0.01} // Allows increments of 0.01 for decimal precision
                                    max={Math.pow(10, 12) - 1}
                                    maxLength={12}
                                    disabled={shouldDisableOriginEditing && lineItem.isOrigin}
                                    onBlur={(e) => {
                                      const value = parseFloat(e.target.value);
                                      if (!isNaN(value) && value > 0 && campaignBudget > 0) {
                                        const percentage = (value / campaignBudget * 100).toFixed(1);
                                        const updatedCampaigns = campaignsFromProps.map((campaign, idx) =>
                                          idx === index ? { ...campaign, budgetPercentage: percentage } : campaign
                                        );
                                        helpers.setFieldsValue({ campaigns: updatedCampaigns });
                                      }
                                    }}
                                  />
                                </Form.Item>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'budgetPercentage']}
                                  fieldKey={[fieldKey, 'budgetPercentage']}
                                  style={{ width: '30%' }}
                                >
                                  <InputNumber
                                    addonAfter={<>%</>}
                                    min={0}
                                    max={100}
                                    step={0.1}
                                    disabled={shouldDisableOriginEditing && lineItem.isOrigin}
                                    onBlur={(e) => {
                                      const percentage = parseFloat(e.target.value);
                                      if (!isNaN(percentage) && percentage > 0 && campaignBudget > 0) {
                                        const budgetTotal = parseFloat((percentage / 100 * campaignBudget).toFixed(2));
                                        const updatedCampaigns = campaignsFromProps.map((campaign, idx) =>
                                          idx === index ? { ...campaign, budgetTotal } : campaign
                                        );
                                        helpers.setFieldsValue({ campaigns: updatedCampaigns });
                                      }
                                    }}
                                  />
                                </Form.Item>
                              </Input.Group>
                            </div>
                          </Space>
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  <Row gutter={20}>
                    <Col span={10}>
                      <Row>
                        {!hideCpmFields && (
                          <Col span={14}>
                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                              <Text>CPM</Text>
                              <Text type="secondary">Rate per 1,000 impressions</Text>
                              <div>
                                <Input.Group compact>
                                  <Tooltip title={!canEditCpm ? "Only Ad Ops users can edit CPM" : ""}>
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'cpm']}
                                      fieldKey={[fieldKey, 'cpm']}
                                      style={{ width: '75%' }}
                                      rules={[
                                        { required: !hideCpmFields, message: 'Missing CPM' },
                                        () => ({
                                          validator(_, value) {
                                            // Skip validation for Percentage pricing model
                                            if (hideCpmFields) {
                                              return Promise.resolve();
                                            }

                                            // Allow empty values (required rule handles this)
                                            if (value === '' || value === null || value === undefined) {
                                              return Promise.resolve();
                                            }

                                            // Check if advertiser is selected
                                            if (!selectedAdvertiser) {
                                              return Promise.reject(
                                                new Error('Please select an advertiser first')
                                              );
                                            }

                                            // Get minimum CPM from advertiser for this campaign type
                                            const minimumCpm = getMinimumCpmForCampaignType(selectedAdvertiser, campaignType);

                                            // Validate against minimum
                                            if (value < minimumCpm) {
                                              const campaignTypeLabel = campaignType === 1 ? 'Display' : campaignType === 7 ? 'External' : campaignType === 8 ? 'Video' : 'this campaign type';
                                              return Promise.reject(
                                                new Error(`CPM must be at least $${minimumCpm.toFixed(2)} (advertiser minimum for ${campaignTypeLabel})`)
                                              );
                                            }

                                            return Promise.resolve();
                                          },
                                        }),
                                      ]}
                                    >
                                      <InputNumber
                                        addonBefore={<>USD</>}
                                        max={Math.pow(10, 12) - 1}
                                        maxLength={12}
                                        disabled={!canEditCpm}
                                      />
                                    </Form.Item>
                                  </Tooltip>
                                </Input.Group>
                              </div>
                            </Space>
                          </Col>
                        )}

                        {!hideCpmFields && (
                          <Col span={10}>
                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                              <Text>Impressions</Text>
                              <Text type="secondary">Per line item</Text>
                              <div>
                                <Input.Group compact>
                                  <Input
                                    disabled
                                    value={(() => {
                                      const budget = helpers.getFieldValue(['campaigns', index, 'budgetTotal']) || 0;
                                      const cpm = helpers.getFieldValue(['campaigns', index, 'cpm']) || 0;
                                      const impressions = calculateImpressions({ budget, cpm });
                                      return impressions.toLocaleString();
                                    })()}
                                  />
                                </Input.Group>
                              </div>
                            </Space>
                          </Col>
                        )}
                      </Row>
                    </Col>
                  </Row>
                </Space>
              </Card>

              <Card title="TARGETING TYPE">
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <Text>Targeting type for this line item: {lineItemName}</Text>
                  <Form.Item
                    {...restField}
                    name={[name, 'targetingType']}
                    fieldKey={[fieldKey, 'targetingType']}
                    rules={[
                      {
                        required: true,
                        message: `Please select a targeting type for ${lineItemName}`,
                      },
                    ]}
                  >
                    <Radio.Group
                      disabled={shouldDisableOriginEditing && lineItem.isOrigin}
                      onChange={(e) => {
                        const newTargetingType = e.target.value;
                        setTargetingType(newTargetingType);

                        // CRITICAL FIX: Clear previous targeting data when switching targeting types
                        // This ensures clean state when switching between REGION/LOCAL/COUNTRY
                        const updatedCampaigns = [...campaignsFromProps];
                        const currentCampaign = { ...updatedCampaigns[index] };

                        // Clear all targeting data regardless of current type
                        currentCampaign.targetingRegions = [];
                        currentCampaign.targetingLocations = [];
                        currentCampaign.targetingCountries = null;

                        // Update the targeting type
                        currentCampaign.targetingType = newTargetingType;
                        updatedCampaigns[index] = currentCampaign;

                        // Update form values
                        helpers.setFieldsValue({
                          campaigns: updatedCampaigns,
                        });

                      }}
                    >
                      <Space direction="vertical">
                        <Radio value="REGION">Target specific regions</Radio>
                        <Radio value="LOCAL">Local Targeting</Radio>
                        <Radio value="COUNTRY">Target an entire country</Radio>
                      </Space>
                    </Radio.Group>
                  </Form.Item>
                </Space>

                {targetingType === 'REGION' && (
                  <CampaignRequestorTargetingRegionTable
                    name={name}
                    restField={restField}
                    fieldKey={fieldKey}
                    form={helpers.form}
                    campaignIndex={campaignIndex}
                    lineItemIndex={index}
                    editable={!(shouldDisableOriginEditing && lineItem.isOrigin)}
                    setShowRegionsModal={setShowRegionsModal}
                    setPreviousValues={(values) => helpers.setFieldsValue(values)}
                  />
                )}

                {targetingType === 'LOCAL' && (
                  <CampaignRequestorTargetingLocalTable
                    name={name}
                    restField={restField}
                    fieldKey={fieldKey}
                    form={helpers.form}
                    campaignIndex={campaignIndex}
                    lineItemIndex={index}
                    setShowTargetingGroupsModal={setShowTargetingGroupsModal}
                    editable={!(shouldDisableOriginEditing && lineItem.isOrigin)}
                    setPreviousValues={(values) => helpers.setFieldsValue(values)}
                  />
                )}

                {targetingType === 'COUNTRY' && (
                  <CampaignRequestorCountriesTable
                    form={helpers.form}
                    restField={restField}
                    name={name}
                    fieldKey={fieldKey}
                    editable={!(shouldDisableOriginEditing && lineItem.isOrigin)}
                    campaignIndex={campaignIndex}
                    lineItemIndex={index}
                  />
                )}
              </Card>

              <CampaignRequestorAudience
                form={helpers.form}
                index={index}
                campaignIndex={campaignIndex}
                restField={restField}
                fieldKey={fieldKey}
                name={name}
                editable={!(shouldDisableOriginEditing && lineItem.isOrigin)}
                setPreviousValues={(values) => helpers.setFieldsValue(values)}
              />

              {campaignType === 8 ? (
                <CampaignRequestorVideoCreatives
                  form={helpers.form}
                  index={index}
                  campaignIndex={campaignIndex}
                  restField={restField}
                  fieldKey={fieldKey}
                  name={name}
                  editable={!(shouldDisableOriginEditing && lineItem.isOrigin)}
                  setPreviousValues={(values) => helpers.setFieldsValue(values)}
                />
              ) : (
                <CampaignRequestorAdGroup
                  form={helpers.form}
                  index={index}
                  campaignIndex={campaignIndex}
                  lineItem={lineItem}
                  campaignType={campaignType}
                  restField={restField}
                  fieldKey={fieldKey}
                  name={name}
                  editable={!(shouldDisableOriginEditing && lineItem.isOrigin)}
                  setPreviousValues={(values) => helpers.setFieldsValue(values)}
                />
              )}
                    </Space>
                  ),
                },
                // DSP Settings tab - only shown when Advanced Mode and Nexxen DSP
                ...(isAdvancedMode && campaignDsp === 'NEXXEN' ? [{
                  key: 'dsp',
                  label: 'DSP Settings',
                  children: (
                    <Space direction="vertical" style={{ width: '100%', paddingBottom: 24 }} size="large">
                      {/* Flight Schedule */}
                      <Card title="FLIGHT SCHEDULE">
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                            <div>
                              <Text type="secondary" style={{ fontSize: 13 }}>
                                Configure multiple flight date ranges for phased execution
                              </Text>
                            </div>
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              onClick={() => {
                                let newStartDate, newEndDate;

                                // If no existing flight schedules, use line item's start and end dates
                                if (flightSchedule.length === 0) {
                                  // First flight: use line item dates (already in UTC from timezone-aware pickers)
                                  newStartDate = lineItemStartDate
                                    ? moment(lineItemStartDate)
                                    : moment.tz(timezone).startOf('day').utc();
                                  newEndDate = lineItemEndDate
                                    ? moment(lineItemEndDate)
                                    : moment.tz(timezone).add(2, 'days').endOf('day').utc();
                                } else {
                                  // Subsequent flights: find the furthest end date and add after it
                                  let furthestEndDate = null;
                                  flightSchedule.forEach((flight) => {
                                    if (flight.endDate) {
                                      const flightEnd = moment(flight.endDate);
                                      if (!furthestEndDate || flightEnd.isAfter(furthestEndDate)) {
                                        furthestEndDate = flightEnd;
                                      }
                                    }
                                  });

                                  // If no furthest end date found, use line item end date or today
                                  if (!furthestEndDate) {
                                    furthestEndDate = lineItemEndDate ? moment(lineItemEndDate) : moment.tz(timezone);
                                  }

                                  // Convert to user timezone to get the calendar date, then add 1 day, convert back to UTC
                                  const nextDay = moment(furthestEndDate).tz(timezone).add(1, 'day').format('YYYY-MM-DD');
                                  newStartDate = moment.tz(nextDay, timezone).startOf('day').utc();
                                  newEndDate = moment.tz(nextDay, timezone).add(2, 'days').endOf('day').utc();
                                }

                                // Build new flight object
                                const newFlight = {
                                  startDate: newStartDate,
                                  endDate: newEndDate,
                                  budget: null,
                                };

                                // Only add CPM for CPM pricing model
                                if (!hideCpmFields) {
                                  newFlight.cpm = lineItem.cpm || 7;
                                }

                                // Create updated flight schedule with the new flight
                                const updatedFlightSchedule = [
                                  ...flightSchedule,
                                  newFlight,
                                ];

                                setFlightSchedule(updatedFlightSchedule);

                                // Auto-adjust parent end dates if the new flight's end date exceeds them
                                autoAdjustEndDatesFromFlightSchedule(updatedFlightSchedule);
                              }}
                            >
                              Add Flight
                            </Button>
                          </Space>

                          <Table
                            dataSource={flightSchedule.map((flight, idx) => {
                              const numberOfDays = flight.startDate && flight.endDate
                                ? moment(flight.endDate).diff(moment(flight.startDate), 'days') + 1
                                : null;
                              const impressionGoal = flight.budget && flight.cpm
                                ? Math.round((flight.budget / flight.cpm) * 1000)
                                : null;
                              return {
                                key: idx,
                                ...flight,
                                numberOfDays,
                                impressionGoal,
                              };
                            })}
                            columns={[
                              {
                                title: <span>Start Date <Text type="danger">*</Text></span>,
                                dataIndex: 'startDate',
                                key: 'startDate',
                                width: 150,
                                render: (value, _, idx) => (
                                  <DatePicker
                                    style={{
                                      width: '100%',
                                      borderColor: !value ? '#ff4d4f' : undefined
                                    }}
                                    value={value ? moment(value).tz(timezone) : null}
                                    format="MM/DD/YYYY"
                                    placeholder="Start date"
                                    status={!value ? 'error' : undefined}
                                    onChange={(date) => {
                                      const updated = [...flightSchedule];
                                      const normalizedDate = date ? moment.tz(date.format('YYYY-MM-DD'), timezone).startOf('day').utc() : null;
                                      updated[idx] = { ...updated[idx], startDate: normalizedDate };
                                      setFlightSchedule(updated);
                                    }}
                                  />
                                ),
                              },
                              {
                                title: <span>End Date <Text type="danger">*</Text></span>,
                                dataIndex: 'endDate',
                                key: 'endDate',
                                width: 150,
                                render: (value, _, idx) => (
                                  <DatePicker
                                    style={{
                                      width: '100%',
                                      borderColor: !value ? '#ff4d4f' : undefined
                                    }}
                                    value={value ? moment(value).tz(timezone) : null}
                                    format="MM/DD/YYYY"
                                    placeholder="End date"
                                    status={!value ? 'error' : undefined}
                                    onChange={(date) => {
                                      const updated = [...flightSchedule];
                                      const normalizedDate = date ? moment.tz(date.format('YYYY-MM-DD'), timezone).endOf('day').utc() : null;
                                      updated[idx] = { ...updated[idx], endDate: normalizedDate };
                                      setFlightSchedule(updated);
                                      // Auto-adjust parent end dates if flight schedule end date is greater
                                      autoAdjustEndDatesFromFlightSchedule(updated);
                                    }}
                                  />
                                ),
                              },
                              {
                                title: <span>Budget <Text type="danger">*</Text></span>,
                                dataIndex: 'budget',
                                key: 'budget',
                                width: 150,
                                render: (value, record, idx) => {
                                  // Check if this flight is restricted (starts within 1 hour) for existing launched line items
                                  const isExistingLineItem = !!lineItem.dspLineItemId;
                                  const flightRestricted = isExistingLineItem && isFlightBudgetRestricted(record);

                                  const inputElement = (
                                    <InputNumber
                                      style={{
                                        width: '100%',
                                        borderColor: (!value || value <= 0) ? '#ff4d4f' : undefined,
                                        backgroundColor: flightRestricted ? '#f5f5f5' : undefined,
                                      }}
                                      value={value}
                                      placeholder="500"
                                      prefix="$"
                                      min={0}
                                      precision={2}
                                      disabled={flightRestricted}
                                      status={(!value || value <= 0) ? 'error' : undefined}
                                      formatter={val => val ? `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                                      parser={val => val.replace(/\$\s?|(,*)/g, '')}
                                      onChange={(newValue) => {
                                        const updated = [...flightSchedule];
                                        updated[idx] = { ...updated[idx], budget: newValue };
                                        setFlightSchedule(updated);
                                      }}
                                    />
                                  );

                                  if (flightRestricted) {
                                    return (
                                      <Tooltip
                                        title="Budget cannot be changed for flights starting within 1 hour. Nexxen requires budget changes to be made more than 1 hour before the flight starts."
                                        color="orange"
                                      >
                                        {inputElement}
                                      </Tooltip>
                                    );
                                  }

                                  return inputElement;
                                },
                              },
                              // CPM column - hidden for Percentage pricing
                              ...(!hideCpmFields ? [{
                                title: <span>CPM <Text type="danger">*</Text></span>,
                                dataIndex: 'cpm',
                                key: 'cpm',
                                width: 120,
                                render: (value, _, idx) => (
                                  <InputNumber
                                    style={{
                                      width: '100%',
                                      borderColor: (!value || value < 1) ? '#ff4d4f' : undefined
                                    }}
                                    value={value}
                                    placeholder="10"
                                    min={0}
                                    precision={2}
                                    status={(!value || value < 1) ? 'error' : undefined}
                                    onChange={(newValue) => {
                                      const updated = [...flightSchedule];
                                      updated[idx] = { ...updated[idx], cpm: newValue };
                                      setFlightSchedule(updated);
                                    }}
                                  />
                                ),
                              }] : []),
                              // Impressions column - hidden for Percentage pricing
                              ...(!hideCpmFields ? [{
                                title: 'Impressions',
                                dataIndex: 'impressionGoal',
                                key: 'impressionGoal',
                                width: 120,
                                align: 'right',
                                render: (value) => (
                                  <Text>{value ? value.toLocaleString() : '-'}</Text>
                                ),
                              }] : []),
                              {
                                title: 'Days',
                                dataIndex: 'numberOfDays',
                                key: 'numberOfDays',
                                width: 80,
                                align: 'center',
                                render: (value) => (
                                  <Text>{value !== null ? value : '-'}</Text>
                                ),
                              },
                              {
                                title: '',
                                key: 'actions',
                                width: 50,
                                align: 'center',
                                render: (_, __, idx) => (
                                  <Button
                                    danger
                                    size="small"
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    onClick={() => {
                                      const updated = flightSchedule.filter((_, i) => i !== idx);
                                      setFlightSchedule(updated);
                                    }}
                                  />
                                ),
                              },
                            ]}
                            pagination={false}
                            size="small"
                            locale={{
                              emptyText: 'No flight dates configured. Click "Add Flight" to add a new flight.',
                            }}
                          />
                        </Space>
                      </Card>

                      {/* SSP & Bid Settings */}
                      <Card title="SSP & BID SETTINGS">
                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Row gutter={[16, 16]}>
                      <Col span={6}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <Text>SSP</Text>
                          <Text type="secondary">
                            <small>Supply-Side Platform</small>
                          </Text>
                          <Form.Item
                            {...restField}
                            name={[name, 'supplySidePlatform']}
                            fieldKey={[fieldKey, 'supplySidePlatform']}
                          >
                            <Select
                              style={{ width: '100%' }}
                              showSearch
                              optionFilterProp="children"
                              placeholder="Select SSP"
                              filterOption={(input, option) =>
                                option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                              }
                              options={sspPartners.map((value) => ({
                                value,
                                label: value,
                              }))}
                            />
                          </Form.Item>
                        </Space>
                      </Col>
                      {!hideCpmFields && (
                        <Col span={6}>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text>Maximum Bid</Text>
                            <Text type="secondary">
                              <small>Max bid per CPM</small>
                            </Text>
                            <Form.Item
                              {...restField}
                              name={[name, 'maxBid']}
                              fieldKey={[fieldKey, 'maxBid']}
                            >
                              <InputNumber
                                style={{ width: '100%' }}
                                placeholder="4.00"
                                prefix="$"
                                min={0.01}
                                step={0.01}
                                precision={2}
                              />
                            </Form.Item>
                          </Space>
                        </Col>
                      )}
                      <Col span={6}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <Text>Frequency Cap</Text>
                          <Text type="secondary">
                            <small>Max daily impressions per device</small>
                          </Text>
                          <Form.Item
                            {...restField}
                            name={[name, 'frequencyCap']}
                            fieldKey={[fieldKey, 'frequencyCap']}
                          >
                            <InputNumber
                              style={{ width: '100%' }}
                              placeholder="3"
                              min={1}
                            />
                          </Form.Item>
                        </Space>
                      </Col>
                    </Row>
                  </Space>
                      </Card>

                      {/* Nexxen Segments */}
                      <Card title="NEXXEN SEGMENTS">
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <div>
                      <Text>Third-Party Targeting Segments</Text>
                      <br />
                      <Text type="secondary">
                        <small>Select one Nexxen segment set for audience targeting</small>
                      </Text>
                    </div>
                    {(lineItem.nexxenSegments || []).length > 0 && (
                      <Table
                        dataSource={lineItem.nexxenSegments || []}
                        rowKey="segmentId"
                        size="small"
                        pagination={false}
                        columns={[
                          {
                            title: 'Name',
                            dataIndex: 'name',
                            key: 'name',
                            ellipsis: true,
                          },
                          {
                            title: 'Type',
                            dataIndex: 'segmentType',
                            key: 'segmentType',
                            width: 120,
                            render: (type) => (
                              <Tag color={type === 'segmentSet' ? 'blue' : 'green'}>
                                {type === 'segmentSet' ? 'Segment Set' : 'Segment'}
                              </Tag>
                            ),
                          },
                          {
                            title: 'CPM Cost',
                            dataIndex: 'cost',
                            key: 'cost',
                            width: 100,
                            render: (cost) => (cost > 0 ? `$${cost.toFixed(2)}` : '-'),
                          },
                          {
                            title: '',
                            key: 'action',
                            width: 60,
                            render: (_, record) => (
                              <Button
                                type="text"
                                danger
                                size="small"
                                icon={<DeleteOutlined />}
                                onClick={() => {
                                  const updatedCampaigns = campaignsFromProps.map((campaign, i) => {
                                    if (i === index) {
                                      return {
                                        ...campaign,
                                        nexxenSegments: (campaign.nexxenSegments || []).filter(
                                          (s) => s.segmentId !== record.segmentId
                                        ),
                                      };
                                    }
                                    return campaign;
                                  });
                                  helpers.setFieldsValue({
                                    campaigns: updatedCampaigns,
                                  });
                                }}
                              />
                            ),
                          },
                        ]}
                      />
                    )}
                    <Button
                      style={{ width: 200 }}
                      onClick={() => setShowNexxenSegmentsDialog(true)}
                    >
                      Segment Lookup
                    </Button>
                  </Space>
                      </Card>

                      {/* Inventory & Supply */}
                      <Card title="INVENTORY & SUPPLY">
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <div>
                      <Text strong>Inventory Configuration</Text>
                      <br />
                      <Text type="secondary">
                        Configure inventory sources, site lists, and private deals for this line item
                      </Text>
                    </div>

                    {/* Inventory Sources Table */}
                    {(lineItem.inventorySources?.length > 0) && (
                      <div>
                        <Text strong style={{ fontSize: 13 }}>Inventory Sources (SSPs/Exchanges)</Text>
                        <Table
                          dataSource={lineItem.inventorySources || []}
                          rowKey="sourceId"
                          size="small"
                          pagination={false}
                          style={{ marginTop: 8 }}
                          columns={[
                            {
                              title: 'Name',
                              dataIndex: 'name',
                              key: 'name',
                              ellipsis: true,
                            },
                            {
                              title: '',
                              key: 'action',
                              width: 60,
                              render: (_, record) => (
                                <Button
                                  type="text"
                                  danger
                                  size="small"
                                  icon={<DeleteOutlined />}
                                  onClick={() => {
                                    const updatedCampaigns = campaignsFromProps.map((campaign, i) => {
                                      if (i === index) {
                                        return {
                                          ...campaign,
                                          inventorySources: (campaign.inventorySources || []).filter(
                                            (s) => s.sourceId !== record.sourceId
                                          ),
                                        };
                                      }
                                      return campaign;
                                    });
                                    helpers.setFieldsValue({ campaigns: updatedCampaigns });
                                  }}
                                />
                              ),
                            },
                          ]}
                        />
                      </div>
                    )}

                    {/* Inventory Lists Table */}
                    {(lineItem.inventoryLists?.length > 0) && (
                      <div>
                        <Text strong style={{ fontSize: 13 }}>Inventory Lists (Site/App Lists)</Text>
                        <Table
                          dataSource={lineItem.inventoryLists || []}
                          rowKey="listId"
                          size="small"
                          pagination={false}
                          style={{ marginTop: 8 }}
                          columns={[
                            {
                              title: 'Name',
                              dataIndex: 'name',
                              key: 'name',
                              ellipsis: true,
                            },
                            {
                              title: 'Action',
                              dataIndex: 'action',
                              key: 'action',
                              width: 100,
                              render: (action) => (
                                <Tag color={action === 'include' ? 'green' : 'red'}>
                                  {action === 'include' ? 'Include' : 'Exclude'}
                                </Tag>
                              ),
                            },
                            {
                              title: '',
                              key: 'remove',
                              width: 60,
                              render: (_, record) => (
                                <Button
                                  type="text"
                                  danger
                                  size="small"
                                  icon={<DeleteOutlined />}
                                  onClick={() => {
                                    const updatedCampaigns = campaignsFromProps.map((campaign, i) => {
                                      if (i === index) {
                                        return {
                                          ...campaign,
                                          inventoryLists: (campaign.inventoryLists || []).filter(
                                            (l) => l.listId !== record.listId
                                          ),
                                        };
                                      }
                                      return campaign;
                                    });
                                    helpers.setFieldsValue({ campaigns: updatedCampaigns });
                                  }}
                                />
                              ),
                            },
                          ]}
                        />
                      </div>
                    )}

                    {/* Publisher Deals Table */}
                    {(lineItem.dspDealIds?.length > 0) && (
                      <div>
                        <Text strong style={{ fontSize: 13 }}>Publisher Deals</Text>
                        <Table
                          dataSource={lineItem.dspDealIds || []}
                          rowKey="dealId"
                          size="small"
                          pagination={false}
                          style={{ marginTop: 8 }}
                          columns={[
                            {
                              title: 'Name',
                              dataIndex: 'name',
                              key: 'name',
                              ellipsis: true,
                            },
                            {
                              title: 'Deal ID',
                              dataIndex: 'dealId',
                              key: 'dealId',
                              width: 180,
                              ellipsis: true,
                            },
                            {
                              title: 'CPM Bid',
                              dataIndex: 'cpmBid',
                              key: 'cpmBid',
                              width: 100,
                              render: (cpmBid) => (cpmBid ? `$${cpmBid.toFixed(2)}` : '-'),
                            },
                            {
                              title: '',
                              key: 'action',
                              width: 60,
                              render: (_, record) => (
                                <Button
                                  type="text"
                                  danger
                                  size="small"
                                  icon={<DeleteOutlined />}
                                  onClick={() => {
                                    const updatedCampaigns = campaignsFromProps.map((campaign, i) => {
                                      if (i === index) {
                                        return {
                                          ...campaign,
                                          dspDealIds: (campaign.dspDealIds || []).filter(
                                            (d) => d.dealId !== record.dealId
                                          ),
                                        };
                                      }
                                      return campaign;
                                    });
                                    helpers.setFieldsValue({ campaigns: updatedCampaigns });
                                  }}
                                />
                              ),
                            },
                          ]}
                        />
                      </div>
                    )}

                    {/* Empty state */}
                    {(!lineItem.inventorySources?.length && !lineItem.inventoryLists?.length && !lineItem.dspDealIds?.length) && (
                      <Text type="secondary">No inventory configured (will use defaults)</Text>
                    )}

                    <Button onClick={() => setShowInventoryDialog(true)}>
                      Configure Inventory
                    </Button>
                  </Space>
                      </Card>
                    </Space>
                  ),
                }] : []),
              ]}
            />
          </Card>
        </Panel>
      </Collapse>

      {showRegionsModal && (
        <CampaignRequestorRegionGroupsDialog
          visible={showRegionsModal}
          setVisible={setShowRegionsModal}
          selectedRegionGroups={targetingRegions}
          onAddSelections={(regions) => {
            const updatedCampaigns = campaignsFromProps.map((campaign, i) => {
              if (i === index) {
                return {
                  ...campaign,
                  targetingRegions: regions.map((region) => {
                    // Check if region is newly added
                    const existingRegion = targetingRegions.find((r) => r.name === region.name);

                    if (existingRegion) {
                      return existingRegion;
                    }

                    delete region.id;
                    return region;
                  }),
                };
              }
              return campaign;
            });
            helpers.setFieldsValue({
              campaigns: updatedCampaigns,
            });
          }}
        />
      )}

      {showTargetingGroupsModal && (
        <CampaignRequestorTargetingGroupsDialog
          visible={showTargetingGroupsModal}
          setVisible={setShowTargetingGroupsModal}
          selectedTargetingGroups={targetingLocations}
          onAddSelections={(targetingLocations) => {
            const updatedCampaigns = campaignsFromProps.map((campaign, i) => {
              if (i === index) {
                return {
                  ...campaign,
                  targetingLocations: targetingLocations.map((location) => {
                    // Check if location is newly added
                    const existingLocation = targetingLocations.find(
                      (l) => l.name === location.name
                    );

                    if (existingLocation) {
                      return existingLocation;
                    }

                    delete location.id;
                    return location;
                  }),
                };
              }
              return campaign;
            });
            helpers.setFieldsValue({
              campaigns: updatedCampaigns,
            });
          }}
        />
      )}


      {showNexxenSegmentsDialog && (
        <CampaignRequestorNexxenSegmentsDialog
          visible={showNexxenSegmentsDialog}
          setVisible={setShowNexxenSegmentsDialog}
          selectedSegments={lineItem.nexxenSegments || []}
          onAddSelections={(segments) => {
            const updatedCampaigns = campaignsFromProps.map((campaign, i) => {
              if (i === index) {
                // Replace existing segment with the newly selected one (only one allowed)
                return {
                  ...campaign,
                  nexxenSegments: segments,
                };
              }
              return campaign;
            });
            helpers.setFieldsValue({
              campaigns: updatedCampaigns,
            });
          }}
        />
      )}

      {showInventoryDialog && (
        <CampaignRequestorInventoryDialog
          visible={showInventoryDialog}
          setVisible={setShowInventoryDialog}
          selectedInventorySources={lineItem.inventorySources || []}
          selectedInventoryLists={lineItem.inventoryLists || []}
          selectedPublisherDeals={lineItem.dspDealIds || []}
          campaignType={campaignType}
          onSaveInventoryConfig={({ sources, lists, deals }) => {
            const updatedCampaigns = campaignsFromProps.map((campaign, i) => {
              if (i === index) {
                return {
                  ...campaign,
                  inventorySources: sources,
                  inventoryLists: lists,
                  dspDealIds: deals,
                };
              }
              return campaign;
            });
            helpers.setFieldsValue({
              campaigns: updatedCampaigns,
            });
          }}
        />
      )}
    </div>
  );
};

export default CampaignRequestorLineItem;